<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="xai-free-trial-form" :class="{ 'xai-free-trial-form--otp': hasSentVerificationCode }">
    <UtdOptionsCard class="primary-card">
      <template #content>
        <div class="xai-free-trial-form__container">
          <div class="xai-free-trial-form__inner-container">
            <form @submit.prevent="hasSentVerificationCode ? handleVerify() : handleContinue()">
              <UtdNotification v-if="isServerErrorNotificationVisible"
                               id="xaiServerErrorNotification"
                               :has-close-notification="true"
                               notification-mode="error"
                               @closed="isServerErrorNotificationVisible = false">
                <template #title>
                  <h4>Submission Error</h4>
                </template>
                <div class="xai-free-trial-form__server-error-notification">
                  <p>{{ generalError }}</p>
                </div>
              </UtdNotification>

              <div v-show="!hasSentVerificationCode" class="xai-free-trial-form__fields-wrapper">
                <fieldset class="wk-js2 xai-free-trial-form-field">
                  <UtdTextInput label="Email Address"
                                description="Your email will be your username"
                                :is-error="isEmailInvalid() && isDirty.email"
                                :error-message="emailErrorMessage()"
                                :required="true">
                    <input id="xaiEmailInput"
                           v-model="form.email"
                           aria-label="Email Address"
                           type="email"
                           class="wk-field-input form-field jumpstart-input"
                           autocomplete="email"
                           @input="handleEmailInput"
                           @blur="handleEmailBlur">
                  </UtdTextInput>
                </fieldset>

                <XaiExistingAccountNotification v-if="isEmailNotEligible && !isExistingAccountNotificationDismissed"
                                                :sign-in-url="existingAccountSignInUrl"
                                                @closed="isExistingAccountNotificationDismissed = true" />

                <XaiFreeTrialUnavailableNotification v-if="isFreeTrialUnavailable && !isFreeTrialUnavailableDismissed"
                                                     @closed="isFreeTrialUnavailableDismissed = true" />

                <fieldset v-if="isNpiFieldVisible" class="wk-js2 xai-free-trial-form-field">
                  <UtdTextInput label="NPI"
                                :is-error="isNpiInvalid() && isDirty.npi"
                                :error-message="npiErrorMessage()"
                                :required="true">
                    <input id="xaiNpiInput"
                           v-model="form.npi"
                           aria-label="NPI"
                           type="text"
                           inputmode="numeric"
                           class="wk-field-input form-field jumpstart-input"
                           autocomplete="off"
                           @input="handleNpiInput"
                           @blur="handleNpiBlur">
                  </UtdTextInput>
                </fieldset>

                <XaiNpiUnavailableNotification v-if="isNpiTrialUnavailable && !isNpiTrialUnavailableDismissed"
                                               @closed="isNpiTrialUnavailableDismissed = true" />

                <template v-if="isPasswordFieldVisible">
                  <fieldset class="wk-js2 xai-free-trial-form-field">
                    <UtdPasswordInput :label="$t('LOGIN.PASSWORD')"
                                      :is-error="isPasswordInvalid() && hasPasswordFocused"
                                      :error-message="$t(passwordErrorMessage())"
                                      :required="true"
                                      @text-input-toggle-show="togglePasswordVisible">
                      <input id="xaiPasswordInput"
                             v-model="form.password"
                             :type="passwordInputType"
                             aria-label="Password"
                             class="wk-field-input form-field jumpstart-input"
                             autocomplete="new-password"
                             @focus="hasPasswordFocused = true"
                             @input="isDirty.password = false"
                             @blur="isDirty.password = true">
                    </UtdPasswordInput>
                  </fieldset>

                  <fieldset v-if="hasPasswordFocused"
                            class="xai-free-trial-form__password-rules">
                    <XaiPasswordRules :password="form.password"
                                      :visible="true" />
                  </fieldset>
                </template>
              </div>

              <div v-if="hasSentVerificationCode"
                   class="xai-free-trial-form__otp-section">
                <div class="xai-free-trial-form__otp-header">
                  <h1 class="xai-free-trial-form__otp-title">
                    Verify Email
                  </h1>
                  <p class="xai-free-trial-form__otp-subtitle">
                    We just sent an email to {{ form.email }}. if you don't see the email, please check your spam folder
                  </p>
                </div>
                <div v-if="hasResentVerificationCode"
                     class="xai-free-trial-form__resend-code-text">
                  <i class="wkce-icon-filled-check-circle" />
                  <p>A new code has been sent</p>
                </div>
                <div v-if="isAuthErrorNotificationVisible"
                     class="xai-free-trial-form__two-factor-notification">
                  <UtdNotification id="xaiOtpErrorNotification"
                                   :has-close-notification="true"
                                   notification-mode="error"
                                   @closed="isAuthErrorNotificationVisible = false">
                    <template #title>
                      <h4>Submission Error</h4>
                    </template>
                    <template #listContent>
                      <div class="xai-free-trial-form__auth-notification-list">
                        <p v-for="(message, index) in UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES"
                           :key="index"
                           class="xai-free-trial-form__auth-notification-list-item"
                           v-html="message" />
                      </div>
                    </template>
                  </UtdNotification>
                </div>
                <div class="xai-free-trial-form__otp-input-wrapper">
                  <UtdVerificationCodeInput ref="otpInputRef"
                                            v-model="form.verificationCode"
                                            :has-error="isVerificationCodeInvalid() && isDirty.verificationCode"
                                            @submit="handleVerify" />
                  <p class="xai-free-trial-form__otp-hint">
                    Enter the 6 digit code that appears in your email
                  </p>
                  <p v-if="isVerificationCodeInvalid() && isDirty.verificationCode"
                     class="xai-free-trial-form__otp-error">
                    {{ verificationCodeErrorMessage() }}
                  </p>
                </div>
                <fieldset class="xai-free-trial-form__resend-action wk-js2 xai-free-trial-form-field">
                  <UtdButton id="xaiSubmitVerificationButton"
                             :disabled="isVerificationCodeInvalid() || isSubmitting"
                             button-color="green-shade-3"
                             button-size="large"
                             type="submit">
                    Verify
                  </UtdButton>
                  <UtdButton id="xaiResendVerificationButton"
                             button-style="ghost"
                             button-size="large"
                             type="button"
                             :disabled="isSendNewCodeDisabled || isSubmitting"
                             @click.prevent="handleResendCode">
                    Re-send Email
                  </UtdButton>
                </fieldset>
              </div>
            </form>
          </div>
        </div>
      </template>
      <template #actions>
        <UtdButton v-if="!hasSentVerificationCode"
                   id="xaiContinueButton"
                   :disabled="isSubmitting || !isPasswordFieldVisible || form.password.trim() === ''"
                   button-color="green-shade-3"
                   button-size="large"
                   type="submit"
                   @click="handleContinue">
          Send Verification Email
        </UtdButton>
      </template>
    </UtdOptionsCard>
  </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick } from 'vue';
import UtdOptionsCard from '_acaSrc/components/shared/stdlib/UtdOptionsCard.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdVerificationCodeInput from '_acaSrc/components/shared/stdlib/UtdVerificationCodeInput.vue';
import UtdPasswordInput from '_acaSrc/components/shared/input/UtdPasswordInput.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import XaiPasswordRules from '_acaSrc/components/register/xaiFreeTrial/XaiPasswordRules.vue';
import XaiExistingAccountNotification from '_acaSrc/components/register/xaiFreeTrial/XaiExistingAccountNotification.vue';
import XaiFreeTrialUnavailableNotification from '_acaSrc/components/register/xaiFreeTrial/XaiFreeTrialUnavailableNotification.vue';
import XaiNpiUnavailableNotification from '_acaSrc/components/register/xaiFreeTrial/XaiNpiUnavailableNotification.vue';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import { validatePassword } from '_acaSrc/utility/Validators';
import {
    emailRegex,
    UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES,
    REGISTRATION_FIELD_ERROR
} from '_acaSrc/utility/constants';
import { checkEmailEligibility, checkNpiValidity, EMAIL_CHECK_RESULT } from './useXaiFreeTrialValidation.composable.js';
import { useStore } from 'vuex';

const store = useStore();

const MAX_SEND_CODE_ATTEMPTS = 5;
const NPI_REGEX = /^\d{10}$/;

const form = reactive({
    npi: '',
    email: '',
    password: '',
    verificationCode: ''
});

const isDirty = reactive({
    npi: false,
    email: false,
    password: false,
    verificationCode: false
});

const isNpiFieldVisible = ref(false);
const isPasswordFieldVisible = ref(false);
const isCheckingEmail = ref(false);
const isCheckingNpi = ref(false);
const isEmailNotEligible = ref(false);
const isExistingAccountNotificationDismissed = ref(false);
const existingAccountSignInUrl = ref('/login');
const isFreeTrialUnavailable = ref(false);
const isFreeTrialUnavailableDismissed = ref(false);
const isNpiTrialUnavailable = ref(false);
const isNpiTrialUnavailableDismissed = ref(false);

const passwordInputType = ref('password');
const hasPasswordFocused = ref(false);
const togglePasswordVisible = (hideText) => {
    passwordInputType.value = hideText ? 'password' : 'text';
};

const emit = defineEmits([ 'otp-step-start' ]);

const hasSentVerificationCode = ref(false);
const hasResentVerificationCode = ref(false);
const otpInputRef = ref(null);
const isSubmitting = ref(false);
const isAuthNotificationVisible = ref(false);
const isAuthErrorNotificationVisible = ref(false);
const isServerErrorNotificationVisible = ref(false);
const generalError = ref('');
const otcServerError = ref('');
const sendCodeAttempts = ref(0);
const latestSsoCheckRequestId = ref(0);
const latestNpiCheckRequestId = ref(0);

const isSendNewCodeDisabled = computed(() => sendCodeAttempts.value >= MAX_SEND_CODE_ATTEMPTS);

const isNpiInvalid = () => !NPI_REGEX.test(form.npi);

const npiErrorMessage = () => {
    if (!isDirty.npi || !isNpiInvalid()) {
        return '';
    }
    return 'Invalid NPI';
};

const isEmailInvalid = () => !emailRegex.test(form.email);

const emailErrorMessage = () => {
    if (!isDirty.email || !isEmailInvalid()) {
        return '';
    }
    return REGISTRATION_FIELD_ERROR.EMAIL;
};

const handleEmailInput = () => {
    isDirty.email = false;
    isEmailNotEligible.value = false;
    isExistingAccountNotificationDismissed.value = false;
    existingAccountSignInUrl.value = '/login';
    isFreeTrialUnavailable.value = false;
    isFreeTrialUnavailableDismissed.value = false;
    isNpiFieldVisible.value = false;
    isPasswordFieldVisible.value = false;
    form.npi = '';
    form.password = '';
    isDirty.npi = false;
    isDirty.password = false;
    hasPasswordFocused.value = false;
    isNpiTrialUnavailable.value = false;
    isNpiTrialUnavailableDismissed.value = false;
    latestSsoCheckRequestId.value += 1;
};

const handleEmailBlur = async() => {
    isDirty.email = true;
    isEmailNotEligible.value = false;

    if (isEmailInvalid()) {
        return;
    }

    const requestId = latestSsoCheckRequestId.value + 1;
    latestSsoCheckRequestId.value = requestId;

    isCheckingEmail.value = true;
    try {
        const eligibilityResult = await checkEmailEligibility(form.email.trim());

        if (requestId !== latestSsoCheckRequestId.value) {
            return;
        }

        if (eligibilityResult.result === EMAIL_CHECK_RESULT.FREE_TRIAL_UNAVAILABLE) {
            isFreeTrialUnavailable.value = true;
            isNpiFieldVisible.value = false;
            return;
        }

        if (eligibilityResult.result === EMAIL_CHECK_RESULT.EXISTING_ACCOUNT_SSO) {
            isEmailNotEligible.value = true;
            existingAccountSignInUrl.value = `${eligibilityResult.wayfInfo.url}?login_hint=${encodeURIComponent(form.email.trim())}`;
            isNpiFieldVisible.value = false;
            return;
        }

        if (eligibilityResult.result === EMAIL_CHECK_RESULT.NOT_ELIGIBLE) {
            isEmailNotEligible.value = true;
            isNpiFieldVisible.value = false;
            return;
        }

        isNpiFieldVisible.value = true;
    }
    catch {
        generalError.value = REGISTRATION_FIELD_ERROR.EMAIL_CHECK_SERVICE_UNAVAILABLE;
        isServerErrorNotificationVisible.value = true;
    }
    finally {
        isCheckingEmail.value = false;
    }
};

const handleNpiInput = () => {
    isDirty.npi = false;
    isNpiTrialUnavailable.value = false;
    isNpiTrialUnavailableDismissed.value = false;
    isPasswordFieldVisible.value = false;
    form.password = '';
    isDirty.password = false;
    hasPasswordFocused.value = false;
	latestNpiCheckRequestId.value += 1;
};

const handleNpiBlur = async() => {
    isDirty.npi = true;
    isNpiTrialUnavailable.value = false;

    if (isNpiInvalid()) {
        return;
    }
    const requestId = latestNpiCheckRequestId.value + 1;
    latestNpiCheckRequestId.value = requestId;
    isCheckingNpi.value = true;
    try {
        const result = await checkNpiValidity(form.npi);
		 if (requestId !== latestNpiCheckRequestId.value) {
            return;
        }
        if (!result.isValid) {
            isNpiTrialUnavailable.value = true;
            isPasswordFieldVisible.value = false;
        } else {
            isPasswordFieldVisible.value = true;
        }
    }
    catch {
        isPasswordFieldVisible.value = false;
        generalError.value = 'An error occurred verifying your NPI. Please try again.';
        isServerErrorNotificationVisible.value = true;
    }
    finally {
        isCheckingNpi.value = false;
    }
};

const isPasswordInvalid = () =>
    form.password.trim() === '' || validatePassword(form.password, form.email) !== '';

const passwordErrorMessage = () => {
    if (!isDirty.password || !isPasswordInvalid()) {
        return '';
    }
    return validatePassword(form.password, form.email) || 'MYACCOUNT.PASSWORD_INVALID_LENGTH';
};

const isVerificationCodeInvalid = () =>
    form.verificationCode.trim() === '' || otcServerError.value !== '';

const verificationCodeErrorMessage = () => {
    if (otcServerError.value) {
        return otcServerError.value;
    }
    if (isVerificationCodeInvalid()) {
        return REGISTRATION_FIELD_ERROR.VERIFICATIONCODE;
    }
    return '';
};

const setAllDirty = () => {
    isDirty.email = true;
    if (isNpiFieldVisible.value) {
        isDirty.npi = true;
    }
    if (isPasswordFieldVisible.value) {
        isDirty.password = true;
    }
};

const isFormValid = () =>
    !isEmailInvalid()
    && !isEmailNotEligible.value
    && isNpiFieldVisible.value
    && !isNpiInvalid()
    && !isNpiTrialUnavailable.value
    && isPasswordFieldVisible.value
    && !isPasswordInvalid();

const buildPayload = () => ({
    formName: 'XAI_FREE_TRIAL',
    npi: form.npi,
    email: form.email,
    registrationUserName: form.email,
    password: form.password,
    verifyPassword: form.password,
    country: 'USA'
});

const handleContinue = async() => {
    setAllDirty();
    if (!isFormValid() || isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    try {
        await store.dispatch('login/sendOtcEmail', form.email);
        hasSentVerificationCode.value = true;
        emit('otp-step-start');
        isAuthNotificationVisible.value = true;
        isServerErrorNotificationVisible.value = false;
        sendCodeAttempts.value += 1;
        nextTick(() => otpInputRef.value?.focus());
    }
    catch {
        generalError.value = 'An error occurred. Please try again.';
        isServerErrorNotificationVisible.value = true;
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleResendCode = async() => {
    if (isSubmitting.value || isSendNewCodeDisabled.value) {
        return;
    }
    isSubmitting.value = true;
    otcServerError.value = '';
    try {
        await store.dispatch('login/sendOtcEmail', form.email);
        sendCodeAttempts.value += 1;
        hasResentVerificationCode.value = true;
        isAuthNotificationVisible.value = true;
    }
    catch {
        // no-op
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleVerifyError = (error) => {
    const errorMessage = error?.data?.errorMessage;
    if (errorMessage) {
        otcServerError.value = errorMessage;
    }
    isAuthErrorNotificationVisible.value = true;
};

const handleVerifyResponse = (response) => {
    if (response.isRedirect) {
        return true;
    }
    if (!response.isSuccess) {
        const msg = response.errorMessage || response.formFields?.[0]?.errorMessage;
        if (msg) {
            otcServerError.value = msg;
        }
        isAuthErrorNotificationVisible.value = true;
    }
    return false;
};

const handleVerify = async() => {
    isDirty.verificationCode = true;
    if (isVerificationCodeInvalid() || isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    try {
        const response = await utdRest('register/xaiMobileSubmit', {
            form: buildPayload(),
            twoFactorCode: form.verificationCode,
            termsAccepted: true
        });
        handleVerifyResponse(response);
    }
    catch (error) {
        handleVerifyError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-ERROR-ON-BLUE-BG-COLOR: @WKCE-RED-SHADE1;

.xai-free-trial-form {
  width: 100%;

  .wk-js2 .wk-field-errored .wk-field-input:not(:hover :focus) {
    border-color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR;
  }

  :deep(.xai-free-trial-form-field) {
    .ds1-ma-0();
    padding: 0;
    border: none;
    width: 100%;
    box-sizing: border-box;
  }

  :deep(.wk-field-body) {
    width: 100%;
    box-sizing: border-box;
  }

  :deep(.wk-field-input.form-field) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
    width: 100%;
    height: 44px;
    box-sizing: border-box;
  }

  :deep(.wk-js2 .wk-field-error) {
    color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR;
  }

  :deep(.options-card__action-area) {
    .ds1-pr-1();
    gap: 24px;
  }

  :deep(.options-card__header) {
    display: block;
  }

  :deep(.wk-field) {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    gap: 0;
    margin-bottom: 0;
    box-sizing: border-box;
  }

  :deep(.wk-field-header) {
    width: 100%;
    height: 22px;
    flex-shrink: 0;
    .ds1-mb-1();
  }

  :deep(.wk-field-label) {
    display: block;
    width: 100%;
    height: 22px;
    line-height: 22px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :deep(.wk-field-tip) {
    .ds1-utd-font();
    .ds1-utd-size-1();
    line-height: 21px;
    .ds1-utd-weight-normal();
    height: 21px;
    width: 100%;
    color: @WKCE-GRAY;
    .ds1-ma-0();
    .ds1-mb-1();
    flex-shrink: 0;
  }
}

.xai-free-trial-form__container {
  width: 100%;
  min-height: 480px;
  box-sizing: border-box;
}

.xai-free-trial-form__fields-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 291px;
  gap: 24px;
  box-sizing: border-box;
}

.xai-free-trial-form__all-fields-required {
  .ds1-mb-1();
  .ds1-utd-size-2();
  .ds1-utd-weight-600();
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}


.xai-free-trial-form__password-rules {
  .ds1-ma-0();
  border: none;
}

.xai-free-trial-form__server-error-notification {
  p {
    margin: 0;
    line-height: 1.5;
  }
}

#xaiServerErrorNotification {
  .ds1-mh-3();
  .ds1-mb-2();
}

.xai-free-trial-form__otp-section {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 219px;
  padding: 81px 16px 0;
  gap: 24px;
  box-sizing: border-box;
}

.xai-free-trial-form__otp-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;
  width: 100%;
}

.xai-free-trial-form__otp-title {
  margin: 0;
  width: 100%;
  height: auto;
  .ds1-utd-font();
  .ds1-utd-size-7();
  line-height: 36px;
  .ds1-utd-weight-normal();
  text-align: center;
  color: @WKCE-GRAY-SHADE1;
  box-sizing: border-box;
}

.xai-free-trial-form__otp-subtitle {
  margin: 0;
  .ds1-utd-size-2();
  color: @WKCE-GRAY-SHADE2;
  overflow-wrap: break-word;
  text-align: center;
}

.xai-free-trial-form__otp-input-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 87px;
  gap: 9px;
  margin: 0;
  box-sizing: border-box;
}

.xai-free-trial-form__otp-hint {
  margin: 0;
  font-size: 13px;
  color: @WKCE-GRAY-SHADE1;
  text-align: center;
}

.xai-free-trial-form__otp-error {
  margin: 0;
  font-size: 13px;
  color: @WKCE-RED;
  text-align: center;
}

.xai-free-trial-form__two-factor-notification {
  .ds1-ph-1();
  .ds1-mb-2();
}

.xai-free-trial-form__auth-notification-list {
  .ds1-mv-1();
  .ds1-pl-3();
  width: 85%;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;

  p {
    margin: 0;
    line-height: 1.5;
  }
}

.xai-free-trial-form__auth-notification-list-item {
  .ds1-mb-compact();
}

.xai-free-trial-form__resend-code-text {
  .ds1-ma-1();
  .ds1-flex-vertical-center();
  .ds1-utd-weight-600();
  .ds1-utd-size-3();
  gap: 4px;
}

.wkce-icon-filled-check-circle {
  color: @DS1-UTD-SUCCESS;
  .ds1-utd-size-5();
}

.xai-free-trial-form__resend-action {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 112px;
  gap: 24px;
  box-sizing: border-box;
  border: none;
  margin: 0;
  padding: 0;

  :deep(.utd-button-size-large) {
    width: 100%;
    height: 44px;
    border-radius: 32px;
    box-sizing: border-box;
  }
}

.xAIMobile {
  .xai-free-trial-form {
    display: flex;
    flex-direction: column;
    flex: 1;

    :deep(.wk-field-input.form-field) {
      background-color: @WKCE-WHITE;
      border-radius: 50px;
      border: 1px solid @WKCE-CREAM;
      .ds1-utd-size-3();
      .ds1-utd-weight-300();
      text-overflow: ellipsis;
      height: 44px;
      width: 100%;
      box-sizing: border-box;
    }

    :deep(.wk-field-input.form-field:focus) {
      border-color: @WKCE-CREAM-SHADE1;
    }

    :deep(.wk-field-label) {
      color: @WKCE-CREAM-SHADE2;
      .ds1-utd-size-2();
      .ds1-utd-weight-600();
      width: 100%;
      height: 22px;
      line-height: 22px;
    }

    :deep(.options-card) {
      display: flex;
      flex-direction: column;
      flex: 1;
      background-color: @WKCE-CREAM-TINT2;
      border: none;
      box-shadow: none;
      border-radius: 0;
    }

    :deep(.options-card__header) {
      display: none;
    }

    :deep(.options-card__content) {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding: 0;
    }

    :deep(.xai-free-trial-form__container) {
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    :deep(.xai-free-trial-form__inner-container),
    :deep(.xai-free-trial-form__inner-container > form) {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    :deep(.xai-free-trial-form__otp-section) {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      padding: 81px 24px 0;
      align-self: stretch;
      flex: 1;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }

    :deep(.xai-free-trial-form__otp-header) {
      flex-shrink: 0;
      padding: 0;
    }

    :deep(.xai-free-trial-form__otp-input-wrapper) {
      flex-shrink: 0;
      margin-top: 0;
      margin-bottom: 0;
      gap: 16px;
    }

    :deep(.utd-verification-code-input) {
      width: 100%;
    }

    :deep(.utd-verification-code-input__digit-group) {
      width: 100%;
      gap: 8px;
      box-sizing: border-box;
    }

    :deep(.utd-verification-code-input__digit) {
      flex: 1 1 0;
      min-width: 0;
      width: auto;
      max-width: 57px;
      aspect-ratio: 5 / 6;
      height: auto;
    }

    :deep(.xai-free-trial-form__resend-action) {
      margin-top: auto;
      .ds1-pt-2();
      .ds1-pb-3();
      .ds1-ph-0();
      width: 100%;
      min-height: 112px;
      gap: 24px;
    }

    :deep(.options-card__action-area) {
      margin-top: auto;
      background-color: @WKCE-CREAM-TINT2;
      justify-content: center;
      .ds1-pa-0();
      .ds1-pt-2();
    }

  }

  .xai-free-trial-form--otp {
    :deep(.options-card),
    :deep(.primary-card) {
      background-color: @WKCE-WHITE;
      width: 100%;
      min-height: 100vh;
      box-sizing: border-box;
    }

    :deep(.options-card__action-area) {
      display: none;
    }
  }

  .xai-free-trial-form {

    :deep(.utd-button-size-large) {
      width: 100%;
      height: 44px;
      border-radius: 32px;
      box-sizing: border-box;
    }

    :deep(.utd-button-color-green-shade-3.utd-button-size-large) {
      background-color: @WKCE-CREAM-SHADE1;

      &:hover {
        background-color: @WKCE-CREAM-SHADE2;
        border-color: @WKCE-CREAM-SHADE2;
      }
    }

    :deep(.utd-button-style-ghost.utd-button-size-large) {
      color: @WKCE-CREAM-SHADE1;
      border-color: @WKCE-CREAM-SHADE1;
      background-color: transparent;

      &:hover {
        background-color: @WKCE-CREAM-SHADE1;
        color: @WKCE-WHITE;
      }
    }
  }
}
</style>
